import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DifferenceIcon from "@mui/icons-material/Difference";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import PublishIcon from "@mui/icons-material/Publish";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TransformIcon from "@mui/icons-material/Transform";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useLocation } from "react-router-dom";

import { trackTrainingPlansV2Event } from "./services/trainingPlansV2Service";
import {
  getGuidedStepDestination,
  TRAINING_PLANS_V2_COMMON_TASK_FLOWS,
} from "./trainingPlansV2CommonTaskFlows";
import { renderFlowStepBody } from "./trainingPlansV2RenderStepBody";
import InlineCoachArticleDialog from "./InlineCoachArticleDialog";

const FLOW_ICONS = {
  PlaylistAddCheck: PlaylistAddCheckIcon,
  Difference: DifferenceIcon,
  GroupAdd: GroupAddIcon,
  Publish: PublishIcon,
  ContentCopy: ContentCopyIcon,
  UploadFile: UploadFileIcon,
  AltRoute: AltRouteIcon,
  Transform: TransformIcon,
  SwapHoriz: SwapHorizIcon,
};

const TrainingPlansV2CommonTasks = ({ navigateWithStagingGuard }) => {
  const location = useLocation();
  const [expandedFlowId, setExpandedFlowId] = React.useState(null);
  const [articleDialogSlug, setArticleDialogSlug] = React.useState(null);

  const startGuidedFlow = React.useCallback(
    (flow) => {
      const dest = getGuidedStepDestination(flow, 0, location, location.search);
      if (dest) {
        trackTrainingPlansV2Event("common_tasks_guided_start", { flowId: flow.id });
        navigateWithStagingGuard(dest);
      }
    },
    [location, navigateWithStagingGuard],
  );

  const toggleReadMore = React.useCallback((flowId) => {
    setExpandedFlowId((prev) => {
      const next = prev === flowId ? null : flowId;
      if (next) {
        trackTrainingPlansV2Event("common_tasks_read_more_open", { flowId });
      }
      return next;
    });
  }, []);

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 2, maxWidth: 720 }}>
        Pick a task to work through in the real Training Plans screens. A guide stays at the bottom:
        it moves you step by step (and keeps you on the right page) while you actually create, assign,
        or publish. Expand <strong>Read more</strong> to see every step in order before you start.
      </Typography>
      <Stack spacing={2}>
        {TRAINING_PLANS_V2_COMMON_TASK_FLOWS.map((f) => {
          const IconComponent = FLOW_ICONS[f.icon] || HomeWorkIcon;
          const expanded = expandedFlowId === f.id;
          return (
            <Card key={f.id} variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <IconComponent color="primary" sx={{ fontSize: 32, flexShrink: 0, mt: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {f.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {f.summary}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      flexWrap="wrap"
                      columnGap={0.5}
                      rowGap={0.5}
                      sx={{ mt: 1 }}
                    >
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => startGuidedFlow(f)}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Start guided task →
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => toggleReadMore(f.id)}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                        aria-expanded={expanded}
                      >
                        {expanded ? "Read less" : "Read more"}
                      </Button>
                      <Tooltip title="Full guide and narration (if published in m45-admin)">
                        <IconButton
                          size="small"
                          aria-label="Open inline help article"
                          onClick={() => {
                            trackTrainingPlansV2Event("common_tasks_inline_article_open", {
                              flowId: f.id,
                              slug: f.helpArticleSlug,
                            });
                            setArticleDialogSlug(f.helpArticleSlug);
                          }}
                        >
                          <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Collapse in={expanded}>
                      <Box
                        sx={{
                          mt: 2,
                          pl: 1.5,
                          borderLeft: 2,
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 700, display: "block", mb: 1 }}
                        >
                          Step-by-step
                        </Typography>
                        <List dense disablePadding>
                          {f.steps.map((step, idx) => (
                            <ListItem key={step.title + idx} alignItems="flex-start" sx={{ py: 0.75, px: 0 }}>
                              <ListItemText
                                primary={
                                  <Typography variant="subtitle2" component="span">
                                    {idx + 1}. {step.title}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="body2" component="span" sx={{ display: "block", mt: 0.5 }}>
                                    {renderFlowStepBody(step.body)}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </Collapse>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
      <InlineCoachArticleDialog
        open={Boolean(articleDialogSlug)}
        slug={articleDialogSlug || undefined}
        onClose={() => setArticleDialogSlug(null)}
      />
    </Box>
  );
};

export default TrainingPlansV2CommonTasks;
